"use client";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  clearAllHolidayFilters,
  removeHolidayFilterItem,
  useHolidayFilter,
} from "@/feature/holiday/search/state/extract-holiday-filter-slice";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import {
  DollarSignIcon,
  FilterIcon,
  ListChecks,
  SearchIcon,
  TagIcon,
  Trash2Icon,
  XIcon,
} from "lucide-react";
import { type ReactNode, useCallback, useMemo, useState } from "react";

function formatDuration(value: string) {
  if (value === "1-3") {
    return "1-3 Days";
  }

  if (value === "4-6") {
    return "4-6 Days";
  }

  return "7+ Days";
}

export default function HolidayFilterPopover() {
  const dispatch = useAppDispatch();
  const { selectedOption, defaultOption } = useAppSelector(useHolidayFilter);
  const [isOpen, setIsOpen] = useState(false);

  const appliedFiltersCount = useMemo(() => {
    let count = 0;

    if (selectedOption.query.trim()) {
      count++;
    }

    if (
      selectedOption.minPrice !== defaultOption.minPrice ||
      selectedOption.maxPrice !== defaultOption.maxPrice
    ) {
      count++;
    }

    count += selectedOption.holidayTypes.length;
    count += selectedOption.durations.length;

    return count;
  }, [defaultOption, selectedOption]);

  const removeFilter = useCallback(
    (key: "query" | "minPrice" | "maxPrice" | "holidayTypes" | "durations", value?: string) => {
      dispatch(removeHolidayFilterItem({ key, value }));
    },
    [dispatch],
  );

  const handleResetAll = useCallback(() => {
    dispatch(clearAllHolidayFilters());
    setIsOpen(false);
  }, [dispatch]);

  const filterSections = useMemo(() => {
    const sections: {
      title: string;
      icon: ReactNode;
      items: { label: string; onRemove: () => void }[];
    }[] = [];

    if (selectedOption.query.trim()) {
      sections.push({
        title: "Search",
        icon: <SearchIcon className="h-3.5 w-3.5" />,
        items: [
          {
            label: `Name: ${selectedOption.query}`,
            onRemove: () => removeFilter("query"),
          },
        ],
      });
    }

    if (
      selectedOption.minPrice !== defaultOption.minPrice ||
      selectedOption.maxPrice !== defaultOption.maxPrice
    ) {
      sections.push({
        title: "Price Range",
        icon: <DollarSignIcon className="h-3.5 w-3.5" />,
        items: [
          {
            label: `BDT ${selectedOption.minPrice?.toLocaleString()} - ${selectedOption.maxPrice?.toLocaleString()}`,
            onRemove: () => {
              removeFilter("minPrice");
              removeFilter("maxPrice");
            },
          },
        ],
      });
    }

    if (selectedOption.holidayTypes.length > 0) {
      sections.push({
        title: "Holiday Type",
        icon: <TagIcon className="h-3.5 w-3.5" />,
        items: selectedOption.holidayTypes.map((type) => ({
          label: type,
          onRemove: () => removeFilter("holidayTypes", type),
        })),
      });
    }

    if (selectedOption.durations.length > 0) {
      sections.push({
        title: "Duration",
        icon: <TagIcon className="h-3.5 w-3.5" />,
        items: selectedOption.durations.map((duration) => ({
          label: formatDuration(duration),
          onRemove: () => removeFilter("durations", duration),
        })),
      });
    }

    return sections;
  }, [defaultOption, removeFilter, selectedOption]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild disabled={appliedFiltersCount === 0}>
        <Button variant="ghost" size="sm" className="mr-2">
          <ListChecks className="h-3.5 w-3.5" />
          <Badge
            className="flex h-4 w-4 items-center justify-center p-0 text-[10px]"
            variant="destructive"
          >
            {appliedFiltersCount}
          </Badge>
        </Button>
      </PopoverTrigger>

      <PopoverContent
        hidden={appliedFiltersCount === 0}
        className="w-72 border-border bg-background p-0 shadow-lg"
        align="end"
        sideOffset={4}
      >
        <div className="border-b border-border p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FilterIcon className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold">Applied Filters</h3>
            </div>
            <div className="flex items-center gap-1">
              {appliedFiltersCount > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleResetAll}
                  className="h-7 text-xs text-destructive hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2Icon className="h-3 w-3" />
                  Clear All
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className="h-7 w-7 p-0"
              >
                <XIcon className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {appliedFiltersCount} filter{appliedFiltersCount > 1 ? "s" : ""} applied
          </p>
        </div>

        <div className="max-h-80 overflow-y-auto bg-muted">
          <div className="space-y-3">
            {filterSections.map((section, sectionIndex) => (
              <div
                key={sectionIndex}
                className="m-1.5 space-y-2 rounded-md border bg-background p-1.5"
              >
                <div className="flex items-center gap-2 px-1">
                  <span className="text-muted-foreground">{section.icon}</span>
                  <h4 className="text-xs font-medium text-foreground">
                    {section.title}
                  </h4>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {section.items.map((item, itemIndex) => (
                    <Badge
                      key={itemIndex}
                      variant="secondary"
                      className="flex max-w-full items-center border-border px-1 text-xs transition-colors"
                    >
                      <span className="max-w-48 truncate text-xs">
                        {item.label}
                      </span>
                      <button
                        type="button"
                        onClick={item.onRemove}
                        className="cursor-pointer rounded-full transition-colors hover:bg-muted"
                      >
                        <XIcon className="h-2.5 w-2.5" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
